import React, { useEffect, useState } from "react";
import { memberApi } from "@/lib/memberAuth";
import { Handshake, MapPin, EnvelopeSimple, Globe } from "@phosphor-icons/react";

export default function MemberMatches() {
  const [me, setMe] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    memberApi.get("/member/matches").then(({ data }) => {
      setMe(data.me || null);
      setMatches(data.matches || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-navy/60" data-testid="portal-matches-loading">Loading…</div>;

  return (
    <div data-testid="portal-matches-page">
      <div className="mb-6">
        <div className="overline mb-2">Introductions</div>
        <h1 className="font-display text-3xl font-bold text-navy tracking-tight">Suggested introductions</h1>
        <p className="text-navy/70 mt-1 text-sm">
          Ranked by shared services and cross-border corridor fit. Reach out directly — every listed member is
          already vetted.
        </p>
      </div>

      {matches.length === 0 ? (
        <div className="border border-dashed border-black/15 p-10 text-center text-navy/60" data-testid="portal-matches-empty">
          No suggested introductions yet. As more members onboard, corridor overlap will surface here.
        </div>
      ) : (
        <div className="space-y-4" data-testid="portal-matches-list">
          {matches.map(({ member, score, shared_services, reason }, i) => (
            <div key={member.id} className="bg-white border border-black/10 p-5 flex items-start gap-5 flex-wrap" data-testid={`portal-match-${member.id}`}>
              <div className="w-12 h-12 bg-navy text-ember flex items-center justify-center shrink-0">
                <Handshake size={22} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-display font-bold text-lg text-navy">
                      {me?.company_name} <span className="text-navy/30 mx-2">→</span> {member.company_name}
                    </div>
                    <div className="text-xs text-navy/60 flex items-center gap-1.5 mt-0.5">
                      <MapPin size={12} /> {member.city ? `${member.city}, ` : ""}{member.country}
                    </div>
                  </div>
                  <span className="ship-number text-xl font-bold text-ember" data-testid={`portal-match-${member.id}-score`}>
                    {score}
                  </span>
                </div>
                <div className="text-sm text-navy/80 mt-3">{reason}</div>
                {shared_services.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {shared_services.map((s) => (
                      <span key={s} className="text-[10px] uppercase tracking-widest bg-ember/10 border border-ember/40 px-1.5 py-0.5 text-navy">{s}</span>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-4 text-sm">
                  {member.contact_email && (
                    <a href={`mailto:${member.contact_email}`} className="flex items-center gap-1.5 text-navy hover:text-ember" data-testid={`portal-match-${member.id}-email`}>
                      <EnvelopeSimple size={14} /> {member.contact_email}
                    </a>
                  )}
                  {member.website && (
                    <a href={member.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-navy hover:text-ember">
                      <Globe size={14} /> {member.website.replace(/^https?:\/\//, "")}
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
